/**
 * AuthModal Component
 *
 * Diamond Architecture: Modal dialog for authentication.
 * Uses shadcn Dialog + OtpFlow for the actual auth forms.
 * Matches Rails legacy: Phone OTP → OR divider → Google Sign-In
 *
 * Auto-closes on route change (Unicorn Standard):
 * - Clicking Terms/Privacy links closes modal
 * - Back button closes modal
 * - Any navigation closes modal
 */
import { AppDivider } from '@/shared/components/app';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import type { GoogleClientId } from '@letsgethome/types/branded';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@letsgethome/ui/dialog';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { GoogleSignIn } from './GoogleSignIn';
import { OtpFlow } from './OtpFlow';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  googleClientId?: GoogleClientId;
}

export function AuthModal({ isOpen, onClose, googleClientId }: AuthModalProps) {
  const tModal = useTranslations('auth.modal');
  const { onAuthSuccess } = useAuthGate();
  const { pathname } = useLocation();
  const initialPathRef = useRef(pathname);

  useEffect(() => {
    if (isOpen && pathname !== initialPathRef.current) {
      onClose();
    }
  }, [pathname, isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      initialPathRef.current = pathname;
    }
  }, [isOpen, pathname]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">{tModal('title')}</DialogTitle>
          <DialogDescription className="text-center">{tModal('subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <OtpFlow />
        </div>

        <AppDivider text={tModal('or')} />

        <div className="w-full">
          {googleClientId && <GoogleSignIn clientId={googleClientId} onSuccess={onAuthSuccess} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
